import { useState, useEffect } from 'react'
import { useQuery, useMutation } from 'react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Euro, 
  Package, 
  CheckCircle,
  ShoppingCart,
  Minus,
  Plus
} from 'lucide-react'
import { apiService as api } from '../services/api'
import ProductImage from '../components/ProductImage'
import ImageGallery from '../components/ImageGallery'
import AddToFavorites from '../components/AddToFavorites'
import { useCatalogSettings } from '../contexts/CatalogContext'
import { useCart } from '../contexts/CartContext'
import { titleToSlug } from '../utils/urlUtils'
import { sanitizeHtml } from '../utils/sanitizeHtml'
import toast from 'react-hot-toast'

const RentmanProduct = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { settings: catalogSettings } = useCatalogSettings()
  const { addToCart } = useCart()
  const [user, setUser] = useState(null)
  const [quantity, setQuantity] = useState(1)
  // Plus de vérification de disponibilité sur cette page


  const { data: adminSettingsData } = useQuery(
    'rentman-settings-public',
    () => api.get('/rentman/settings/public', { params: { t: Date.now() } }).then(res => res.data),
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true
    }
  )

  // Charger les catégories (pour fil d'Ariane)
  const { data: categoriesData } = useQuery(
    'rentman-categories-for-product',
    () => api.get('/rentman/categories').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )
  const backendShowPrice = adminSettingsData?.show_regular_price
  const backendShowAvailability = adminSettingsData?.show_availability
  const backendShowDescription = adminSettingsData?.show_product_description_product
  const showPrice = backendShowPrice === true
  const showAvailability = (backendShowAvailability !== false) && !!catalogSettings.show_product_availability
  const showDescription = (backendShowDescription !== false)

  // Charger l'utilisateur
  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (token) {
      api.get('/auth/me', { params: { t: Date.now() } })
        .then(res => setUser(res.data?.user || null))
        .catch(() => setUser(null));
    }
  }, []);

  // Fonction pour formater les valeurs des spécifications
  const formatSpecValue = (value) => {
    if (value === null || value === undefined) return 'N/A'
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non'
    if (typeof value === 'object') {
      // Pour les objets comme custom_fields, afficher les propriétés non vides
      const nonEmptyProps = Object.entries(value)
        .filter(([_, val]) => val !== null && val !== undefined && val !== '')
        .map(([key, val]) => `${key}: ${val}`)
        .join(', ')
      return nonEmptyProps || 'Aucune donnée'
    }
    return String(value)
  }

  // Récupérer les détails du produit
  const { data: product, isLoading, error } = useQuery(
    ['rentman-product', slug],
    async () => {
      if (!slug) return null
      
      // Récupérer tous les produits (limite élevée pour récupérer tous les produits)
      const response = await api.get('/rentman/products', { 
        params: { 
          t: Date.now(), // Cache-busting
          limit: 1000 // Limite élevée pour récupérer tous les produits
        }
      })
      const products = response.data.products || []
      
      // Chercher le produit dont le slug correspond
      const foundProduct = products.find(p => titleToSlug(p.name) === slug)
      
      if (!foundProduct) {
        throw new Error('Produit non trouvé')
      }
      
      return foundProduct
    },
    {
      enabled: !!slug
    }
  )

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du produit...</p>
        </div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Package className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Produit non trouvé</h2>
          <p className="text-gray-600 mb-4">Le produit demandé n'existe pas</p>
          <button
            onClick={() => navigate('/catalogue-materiels')}
            className="btn-primary"
          >
            Retour au catalogue
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        {/* Navigation */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/catalogue-materiels')}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au catalogue
          </button>
          {(() => {
            const cats = Array.isArray(categoriesData?.categories) ? categoriesData.categories : null
            if (!cats || !product?.category_id) return null
            const byId = new Map(cats.map(c => [String(c.id), c]))
            const trail = []
            let cursor = byId.get(String(product.category_id))
            while (cursor) {
              trail.push(cursor)
              const pid = cursor.parent_id != null ? String(cursor.parent_id) : null
              cursor = pid ? byId.get(pid) : null
            }
            const crumbs = trail.reverse()
            if (!crumbs.length) return null
            return (
              <nav className="text-sm text-gray-500">
                {crumbs.map((c, idx) => (
                  <span key={c.id}>
                    <Link to={`/catalogue-materiels?category_id=${c.id}`} className="hover:text-gray-800">
                      {c.name}
                    </Link>
                    {idx < crumbs.length - 1 && <span className="mx-2">›</span>}
                  </span>
                ))}
              </nav>
            )
          })()}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Colonne gauche: image + description */}
          <div className="space-y-6">
            {/* Image */}
            <div className="bg-white rounded-xl overflow-hidden">
              <ImageGallery
                imageUrl={product.image_url}
                alt={product.name}
                className="w-full h-auto object-contain"
                showDownload={true}
              />
            </div>

            {/* (Description déplacée dans la colonne droite) */}
          </div>

          {/* Colonne droite: Titre + sous-catégorie + prix + disponibilité + specs */}
          <div className="space-y-6">
            <div className="card">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                {product.name}
              </h1>

              {product.category_name && (
                <div className="mb-4">
                  <Link
                    to={`/catalogue-materiels?category_id=${product.category_id || ''}`}
                    className="inline-block bg-orange-100 hover:bg-orange-200 text-orange-800 text-sm font-medium px-3 py-1 rounded-full transition-colors"
                  >
                    {product.category_name}
                  </Link>
                </div>
              )}

              {/* Description (longue) sous la catégorie, au-dessus du prix */}
              {showDescription && (() => {
                const candidates = [
                  product.shop_description_long,
                  product.specifications?.shop_description_long,
                  product.description
                ]
                const picked = candidates.find(v => typeof v === 'string' && v.trim() && v.trim().toLowerCase() !== 'ip')
                if (!picked) return null
                return (
                  <div className="text-gray-700 mb-4">
                    <div
                      className="prose max-w-none leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(picked) }}
                    />
                  </div>
                )
              })()}

              <div className="flex items-center space-x-4 mb-2">
                {showPrice && (
                  <div className="flex items-center space-x-2">
                    <Euro className="h-5 w-5 text-orange-500" />
                    <span className="text-2xl font-bold text-gray-900">
                      {formatPrice(product.price_per_day)}
                    </span>
                    <span className="text-gray-600">/jour</span>
                  </div>
                )}

                {showAvailability && (
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      product.is_available ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                    <span className="text-sm font-medium">
                      {product.is_available ? 'Disponible' : 'Indisponible'}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-3 space-y-3">
                {/* Sélecteur de quantité */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Qté</span>
                  <div className="flex items-center space-x-2">
                    <button 
                      className="btn-outline px-2" 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input 
                      className="w-16 border border-gray-300 rounded px-2 py-1 text-center bg-white text-gray-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" 
                      type="number" 
                      min="1" 
                      value={quantity} 
                      onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                    />
                    <button 
                      className="btn-outline px-2" 
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Bouton Réserver */}
                <button
                  onClick={() => {
                    addToCart({
                      id: product.id,
                      name: product.name,
                      price_per_day: product.price_per_day,
                      image_url: product.image_url
                    }, quantity)
                    toast.success(`${quantity} produit${quantity > 1 ? 's' : ''} ajouté${quantity > 1 ? 's' : ''} à votre liste`)
                  }}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Réserver
                </button>
                
                {/* Bouton Ajouter à ma liste (si utilisateur connecté) */}
                {user && (
                  <AddToFavorites 
                    productId={product.id} 
                    productName={product.name}
                    className="w-full flex items-center justify-center text-sm px-4 py-2 text-logo hover:text-logo-dark transition-colors border border-logo hover:border-logo-dark rounded-md bg-white"
                    buttonText="Ajouter à ma liste"
                  />
                )}
              </div>

              {/* Spécifications - supprimé */}
            </div>

            {/* Informations supplémentaires (bloc d'origine) */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Flightcase, c’est l’avantage pro :
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Réservez en temps réel, sans attente
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Recevez votre devis sous 24/48h
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Bénéficiez d’un support technique dédié
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Accédez à du matériel haut de gamme
                </li>
              </ul>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

export default RentmanProduct
